<!--
  Tells the owner about their plan in the app, not just by email. Nobody else
  sees it, since nobody else can pay.
-->
<template>
  <div
    v-if="status && !hidden"
    class="account-status"
    :class="`account-status--${status.kind}`"
    data-html2canvas-ignore
  >
    <v-icon
      :color="accent"
      size="20"
      class="account-status__icon"
    >
      {{ status.kind === 'grace' ? 'error' : 'schedule' }}
    </v-icon>

    <div class="account-status__text">
      <span class="account-status__title">{{ status.title }}.</span>
      <span class="text-secondary">{{ status.body }}</span>
    </div>

    <v-btn-primary
      size="small"
      class="account-status__action"
      @click="act"
    >
      {{ status.action }}
    </v-btn-primary>

    <v-icon
      v-if="status.dismissible"
      size="18"
      class="account-status__close text-secondary"
      @click="close"
    >
      close
    </v-icon>
  </div>
</template>

<script lang="ts">
import {defineComponent} from "vue"
import {accountStatus, dismiss, isDismissed, type AccountStatus} from "@/utils/accountStatus"

export default defineComponent({
  data() {
    return {
      closed: false,
    }
  },
  computed: {
    status(): AccountStatus | null {
      return accountStatus()
    },
    accent(): string {
      return this.status?.kind === 'grace' ? 'warning' : 'primary'
    },
    hidden(): boolean {
      if (!this.status?.dismissible) return false

      return this.closed || isDismissed(this.status.key)
    },
  },
  watch: {
    // A new key means a new day of the countdown, so it comes back.
    'status.key'() {
      this.closed = false
    },
  },
  methods: {
    close() {
      if (!this.status) return

      dismiss(this.status.key)
      this.closed = true
    },
    // A lapsed account already has a subscription and needs the card fixed in
    // settings; checkout would start a second one. A trial has none.
    act() {
      if (this.status?.kind === 'grace') {
        this.$router.push({
          ...this.$routeTo(this.ROUTES.SETTINGS),
          hash: '#subscription',
        })
        return
      }

      this.$router.push(this.$routeTo(this.ROUTES.PAYMENT, {plan: 'annual'}))
    },
  },
})
</script>

<style scoped lang="scss">
.account-status {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 14px;
  border-radius: 12px;
  background: #fff;
  border: 1px solid rgba(var(--v-theme-primary), 0.18);

  &--grace {
    border-color: rgba(var(--v-theme-warning), 0.45);
    background: rgba(var(--v-theme-warning), 0.06);
  }

  &__icon {
    flex-shrink: 0;
  }

  &__text {
    flex-grow: 1;
    min-width: 0;
    font-size: 0.875rem;
    line-height: 1.35;
  }

  &__title {
    font-weight: 600;
    margin-right: 4px;
  }

  &__action {
    flex-shrink: 0;
  }

  &__close {
    flex-shrink: 0;
    cursor: pointer;
    opacity: 0.6;

    &:hover {
      opacity: 1;
    }
  }

}

// Explicit two rows on a phone. Letting flex wrap put the icon and close
// together and pushed the copy onto its own line.
@media #{map-get(settings.$display-breakpoints, 'xs')} {
  .account-status {
    display: grid;
    grid-template-columns: auto 1fr auto;
    grid-template-areas:
      "icon text  close"
      ".    action action";
    align-items: start;
    row-gap: 10px;

    &__icon {
      grid-area: icon;
      margin-top: 2px;
    }

    &__text {
      grid-area: text;
    }

    &__close {
      grid-area: close;
      margin-top: 2px;
    }

    &__action {
      grid-area: action;
      justify-self: end;
    }
  }
}
</style>
